import React, { useMemo } from 'react';
import styled from 'styled-components';
import { SidebarButton, SidebarHeaderTitle, SidebarTitle } from 'components';
import { theme, toRem } from 'design';
import { SidebarMobileActions } from 'screens/MakePayment/components';
import { ICalculatorPaymentSegment } from 'types';
import { formatDateForUILongFormat } from 'utils/date';
import { Duration } from 'utils/duration';

export interface IDaySegmentSummary {
  segment?: ICalculatorPaymentSegment;
  totalJobMinutes: number;
}

export interface ISummaryProps {
  className?: string;
  date: string;
  primaryActionBtn?: (device: 'mobile' | 'desktop') => React.ReactNode;
  secondaryActionBtn?: (device: 'mobile' | 'desktop') => React.ReactNode;
  segments?: IDaySegmentSummary[];
  totalMinutes: number;
  sidebarOpen?: boolean;
  onExpand?: () => void;
}

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${toRem(40)};

  & > button {
    flex-grow: 1;
  }

  & > * {
    &:not(:last-child) {
      margin-right: ${toRem(16)};
    }
  }
`;

const HeaderText = styled.p`
  font-size: ${toRem(14)};
  font-weight: ${theme.font.weight.medium};
  word-wrap: break-word;
  max-width: 100%;
`;

const SummaryLayout = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  padding: ${toRem(16)} ${toRem(40)};
  background: ${theme.color.neutral[50]};
  border-left: 1px solid ${theme.color.neutral[300]};

  @media (min-width: ${theme.breakpoint.large}px) {
    flex-grow: 1;
    padding: ${toRem(40)};
  }
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-bottom: ${toRem(24)};

  @media (min-width: ${theme.breakpoint.large}px) {
    display: none;
  }
`;

const PaymentDate = styled.p`
  margin-bottom: ${toRem(8)};
  font-size: ${toRem(20)};
  font-weight: 600;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  &:not(:last-child) {
    margin-bottom: ${toRem(8)};
  }
`;

const SegmentText = styled.p`
  font-size: ${toRem(14)};
  color: ${theme.color.neutral[500]};
`;

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${toRem(24)};
`;

const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SegmentSummary = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex-grow: 1;
`;

const TotalHours = styled.p`
  font-size: ${toRem(14)};
`;

const Summary: React.FC<ISummaryProps> = ({ className = '', date, primaryActionBtn, secondaryActionBtn, segments, totalMinutes = 0, sidebarOpen, onExpand }) => {
  const totalFormattedHours = useMemo(() => new Duration(totalMinutes).toString(), [totalMinutes]);
  const segmentSummaries = useMemo(() => {
    if (!segments?.length || !segments[0].segment) return null;

    return segments.map(({ segment, totalJobMinutes }) => (
      <SummarySection key={segment.id}>
        <Row>
          <HeaderText>{segment.name}</HeaderText>
        </Row>
        <Row>
          <SegmentText>Segment Hours</SegmentText>
          <SegmentText>{segment.hours}</SegmentText>
        </Row>
        <Row>
          <SegmentText>Tipped Hours</SegmentText>
          <SegmentText>{new Duration(totalJobMinutes).toString()}</SegmentText>
        </Row>
      </SummarySection>
    ));
  }, [segments]);

  return (
    <SummaryLayout className={className} data-cy="labor-data-summary">
      <MobileHeader>
        <SidebarHeaderTitle>
          <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />
          <SidebarTitle title="Labor Data Summary" />
        </SidebarHeaderTitle>
        <SidebarMobileActions columns={2}>
          {secondaryActionBtn('mobile')}
          {primaryActionBtn('mobile')}
        </SidebarMobileActions>
      </MobileHeader>
      <SummaryWrapper>
        <PaymentDate data-cy="labor-data-payment-date">{formatDateForUILongFormat(date)}</PaymentDate>
        <SummarySection>
          <Row>
            <HeaderText>Tipped Hours</HeaderText>
            <TotalHours data-cy="labor-data-total-tipped-hours">{totalFormattedHours}</TotalHours>
          </Row>
        </SummarySection>
        <SegmentSummary>{segmentSummaries}</SegmentSummary>
      </SummaryWrapper>

      <ButtonsWrapper>
        {secondaryActionBtn('desktop')}
        {primaryActionBtn('desktop')}
      </ButtonsWrapper>
    </SummaryLayout>
  );
};

export default Summary;
